import {
  Card,
  Flex,
  FormatNumber,
  LinkBox,
  LinkOverlay,
  Stat,
  Text,
} from "@chakra-ui/react";
import { MdArrowDownward, MdArrowUpward } from "react-icons/md";
import { Link } from "react-router-dom";

import { IObservation } from "@/types/data";
import ObservationStatus from "@/components/ObservationStatus";
import { GrowthZoneCell } from "@/components/charts";

interface SummaryCardProps {
  linkTo: string;
  title: string;
  subtitle?: string;
  status?: IObservation["status"];
  fragments: number;
  unhealthyFragments: number;
  growth: number | null;
  fragments_count_0_25?: number;
  fragments_count_25_50?: number;
  fragments_count_50_75?: number;
  fragments_count_75_100?: number;
}

function SummaryCard({
  linkTo,
  title,
  subtitle,
  status,
  fragments,
  unhealthyFragments,
  growth,
  fragments_count_0_25 = 0,
  fragments_count_25_50 = 0,
  fragments_count_50_75 = 0,
  fragments_count_75_100 = 0,
}: SummaryCardProps) {
  const unhealthyPct = fragments > 0 ? unhealthyFragments / fragments : 0;
  const hasGrowthZones =
    !!fragments_count_0_25 ||
    !!fragments_count_25_50 ||
    !!fragments_count_50_75 ||
    !!fragments_count_75_100;

  return (
    <LinkBox>
      <Card.Root
        size="sm"
        transition="all 0.2s ease"
        _hover={{ borderColor: "blue.500", bg: "blue.50" }}
      >
        <Card.Header
          fontSize="sm"
          fontWeight="bold"
          color="blue.500"
          display="flex"
          flexDir="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <LinkOverlay asChild>
            <Link to={linkTo}>
              {title}
              {subtitle && (
                <Text fontSize="xs" fontWeight="400" display="inline" ml="2">
                  {subtitle}
                </Text>
              )}
            </Link>
          </LinkOverlay>
          {status && <ObservationStatus status={status} />}
        </Card.Header>
        <Card.Body display="flex" flexDir="column" gap="2">
          <Flex>
            <Stat.Root alignItems="center">
              <Stat.ValueText>{fragments || "-"}</Stat.ValueText>
              <Stat.Label>Fragments</Stat.Label>
            </Stat.Root>
            <Stat.Root alignItems="center">
              <Stat.ValueText alignItems="baseline">
                {status === "processing" ? (
                  "-"
                ) : (
                  <>
                    <Text>{unhealthyFragments}</Text>
                    <Text fontSize="sm" color="fg.muted" lineHeight="initial">
                      (
                      <FormatNumber
                        value={unhealthyPct}
                        // eslint-disable-next-line react/style-prop-object
                        style="percent"
                        maximumFractionDigits={0}
                      />
                      )
                    </Text>
                  </>
                )}
              </Stat.ValueText>
              <Stat.Label>Health Issues</Stat.Label>
            </Stat.Root>
            <Stat.Root alignItems="center">
              <Stat.ValueText alignItems="baseline">
                {growth !== null
                  ? `${Number(growth).toFixed(1).replace(/\.0$/, "")}%`
                  : "-"}
                {growth !== null && (
                  <Text
                    display="inline-block"
                    fontSize="sm"
                    color={growth < 0 ? "fg.warning" : "fg.success"}
                  >
                    {growth > 0 && <MdArrowUpward />}
                    {growth < 0 && <MdArrowDownward />}
                  </Text>
                )}
              </Stat.ValueText>
              <Stat.Label>Growth</Stat.Label>
            </Stat.Root>
          </Flex>
          {hasGrowthZones && (
            <>
              <GrowthZoneCell
                fragments_count_0_25={fragments_count_0_25}
                fragments_count_25_50={fragments_count_25_50}
                fragments_count_50_75={fragments_count_50_75}
                fragments_count_75_100={fragments_count_75_100}
              />
              <Text fontSize="xs" width="full" textAlign="center">
                Growth Zones
              </Text>
            </>
          )}
        </Card.Body>
      </Card.Root>
    </LinkBox>
  );
}

export default SummaryCard;
